import React, { useEffect } from 'react'
import { registerSW } from 'virtual:pwa-register'
import { toast } from 'sonner'

/**
 * Gère la mise à jour du Service Worker (PWA).
 *
 * Utilise l'API officielle de vite-plugin-pwa (registerSW) — un seul point
 * d'enregistrement, cohérent avec `registerType: 'autoUpdate'` de vite.config.
 *
 * Quand une nouvelle version est détectée :
 *  - on active immédiatement le nouveau SW (updateSW(true)),
 *  - on recharge automatiquement la page une fois le SW aux commandes,
 * ce qui garantit que l'utilisateur ne reste jamais bloqué sur une ancienne
 * version dont les chunks lazy ont disparu du serveur.
 */
const PWAUpdatePrompt: React.FC = () => {
  useEffect(() => {
    if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return

    // Évite les boucles de reload : on ne recharge qu'une fois par mise à jour.
    let reloading = false

    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        // Nouvelle version prête → on informe brièvement puis on applique.
        toast('Mise à jour d’Octoveto', {
          description: 'Chargement de la nouvelle version…',
          duration: 2500,
        })
        // Active le nouveau SW ; le reload est déclenché par onControllerChange ci-dessous.
        updateSW(true)
      },
      onRegisteredSW(_swUrl, registration) {
        // Vérifie périodiquement (toutes les 30 min) s'il existe une nouvelle version,
        // pour les sessions longues restées ouvertes.
        if (registration) {
          setInterval(() => registration.update().catch(() => {}), 30 * 60 * 1000)
        }
      },
    })

    // Quand le nouveau SW prend le contrôle, on recharge la page (une seule fois).
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return
      reloading = true
      window.location.reload()
    })
  }, [])

  return null
}

export default PWAUpdatePrompt
